/**
 * Centralized API Service
 * 
 * Provides a consistent interface for all API calls with:
 * - Centralized configuration
 * - Automatic error handling
 * - Request/response logging
 * - Type safety
 * - Timeout management
 * 
 * @example
 * // For Main API calls:
 * import { mainApi } from '../services/api';
 * const users = await mainApi.get('/users');
 * const result = await mainApi.post('/auth/login', { email, password });
 * 
 * @example
 * // For Indexer API calls:
 * import { indexerApi } from '../services/api';
 * const jobs = await indexerApi.get('/jobs');
 * const job = await indexerApi.post('/jobs', { folder_id: '123' });
 * 
 * @example
 * // For raw fetch (when you need custom handling):
 * import { INDEXER_API_URL, INDEXER_API_KEY } from '../config/api';
 * const response = await fetch(`${INDEXER_API_URL}/jobs`, {
 *   headers: { 'X-API-Key': INDEXER_API_KEY }
 * });
 */

import { MAIN_API_URL, MAIN_API_KEY, INDEXER_API_URL, INDEXER_API_KEY } from '../config/api';

interface ApiRequestConfig {
    headers?: Record<string, string>;
    timeout?: number;
}

class ApiError extends Error {
    status?: number;
    data?: unknown;

    constructor(message: string, status?: number, data?: unknown) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.data = data;

        // Maintains proper stack trace for where our error was thrown (only available on V8)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, ApiError);
        }
    }
}

/**
 * API Service class for making HTTP requests with automatic error handling and logging
 * 
 * @class ApiService
 */
class ApiService {
    private baseURL: string;
    private apiKey: string;
    private defaultTimeout: number = 30000;

    /**
     * Creates an API service instance
     * @param {string} baseURL - The base URL for the API
     * @param {string} apiKey - The API key for authentication
     */
    constructor(baseURL: string, apiKey: string) {
        this.baseURL = baseURL;
        this.apiKey = apiKey;
    }

    private async request<T>(
        endpoint: string,
        options: RequestInit = {},
        config: ApiRequestConfig = {}
    ): Promise<T> {
        const url = `${this.baseURL}${endpoint}`;
        const startTime = performance.now();

        try {
            const controller = new AbortController();
            const timeout = setTimeout(
                () => controller.abort(),
                config.timeout || this.defaultTimeout
            );

            // Get user email from localStorage for authenticated requests
            let userEmail: string | null = null;
            try {
                const savedUser = localStorage.getItem('bo_user');
                if (savedUser) {
                    const user = JSON.parse(savedUser);
                    userEmail = user.email;
                }
            } catch (e) {
                console.warn('Failed to get user email from localStorage:', e);
            }

            const response = await fetch(url, {
                ...options,
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-Key': this.apiKey,
                    ...(userEmail ? { 'X-User-Email': userEmail } : {}),
                    ...config.headers,
                    ...options.headers,
                },
                signal: controller.signal,
            });

            clearTimeout(timeout);

            const duration = performance.now() - startTime;

            // Log request in development
            if (import.meta.env.DEV) {
                console.debug(
                    `API ${options.method || 'GET'} ${endpoint}: ${response.status} (${Math.round(duration)}ms)`
                );
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw this.createError(
                    errorData.message || errorData.detail || `HTTP ${response.status}: ${response.statusText}`,
                    response.status,
                    errorData
                );
            }

            return await response.json();
        } catch (error: unknown) {
            if (error instanceof DOMException && error.name === 'AbortError') {
                throw this.createError('Request timeout', 408);
            }

            if (error instanceof ApiError) {
                throw error;
            }

            // Network error
            throw this.createError(
                error instanceof Error ? error.message : 'Network error occurred',
                0
            );
        }
    }

    private createError(message: string, status?: number, data?: unknown): ApiError {
        return new ApiError(message, status, data);
    }

    /**
     * Makes a GET request
     * @template T - The expected response type
     * @param {string} endpoint - The API endpoint (e.g., '/users')
     * @param {ApiRequestConfig} [config] - Optional request configuration
     * @returns {Promise<T>} The response data
     */
    async get<T>(endpoint: string, config?: ApiRequestConfig): Promise<T> {
        return this.request<T>(endpoint, { method: 'GET' }, config);
    }

    /**
     * Makes a POST request
     * @template T - The expected response type
     * @param {string} endpoint - The API endpoint
     * @param {any} [data] - The request body data
     * @param {ApiRequestConfig} [config] - Optional request configuration
     * @returns {Promise<T>} The response data
     */
    async post<T>(
        endpoint: string,
        data?: unknown,
        config?: ApiRequestConfig
    ): Promise<T> {
        return this.request<T>(
            endpoint,
            {
                method: 'POST',
                body: JSON.stringify(data),
            },
            config
        );
    }

    /**
     * Makes a PUT request
     * @template T - The expected response type
     * @param {string} endpoint - The API endpoint
     * @param {any} [data] - The request body data
     * @param {ApiRequestConfig} [config] - Optional request configuration
     * @returns {Promise<T>} The response data
     */
    async put<T>(
        endpoint: string,
        data?: unknown,
        config?: ApiRequestConfig
    ): Promise<T> {
        return this.request<T>(
            endpoint,
            {
                method: 'PUT',
                body: JSON.stringify(data),
            },
            config
        );
    }

    /**
     * Makes a DELETE request
     * @template T - The expected response type
     * @param {string} endpoint - The API endpoint
     * @param {ApiRequestConfig} [config] - Optional request configuration
     * @returns {Promise<T>} The response data
     */
    async delete<T>(endpoint: string, config?: ApiRequestConfig): Promise<T> {
        return this.request<T>(endpoint, { method: 'DELETE' }, config);
    }

    /**
     * Makes a PATCH request
     * @template T - The expected response type
     * @param {string} endpoint - The API endpoint
     * @param {any} [data] - The request body data
     * @param {ApiRequestConfig} [config] - Optional request configuration
     * @returns {Promise<T>} The response data
     */
    async patch<T>(
        endpoint: string,
        data?: unknown,
        config?: ApiRequestConfig
    ): Promise<T> {
        return this.request<T>(
            endpoint,
            {
                method: 'PATCH',
                body: JSON.stringify(data),
            },
            config
        );
    }
}

// Export singleton instances for different APIs
export const mainApi = new ApiService(MAIN_API_URL, MAIN_API_KEY);
export const indexerApi = new ApiService(INDEXER_API_URL, INDEXER_API_KEY);

// Export ApiService and ApiError classes for testing or custom instances
export { ApiError };
export default ApiService;

